import React, {FormEvent, useCallback, useEffect, useMemo, useRef} from "react";
import {useSelector} from "react-redux";
import {CheckboxItem, Text, styled, theme} from "@reside/ui";
import {UserPermissionEnum} from "../../../../utils/types/user";
import {QuicksightDashboard} from "../../../../services/QuicksightService";
import {AppState, select} from "../../../../store";
import {useQuicksightDashboardsPermissionMutation} from "../../hooks/useQuicksightDashboardsPermissionMutation";
import {useQuicksightUsersDashboards} from "../../hooks/useQuicksightUsersDashboards";
import {
  PermissionCategoryCard,
  PermissionCategoryItem,
} from "../permissions-macro-checkbox/PermissionCategoryCard";
import {SyncQuicksightDashboardsButton} from "../sync-quicksight-dashboards-button";
import {syncQuicksightDashboardsReportingPermission} from "./syncQuicksightDashboardsReportingPermission";
import {
  getQuicksightDashboardSelection,
  isQuicksightHeaderStatsReady,
} from "./quicksightDashboardSelection.helpers";
import {
  QuicksightDashboardDeferredSaveHandlers,
  useQuicksightDashboardDraft,
} from "./useQuicksightDashboardDraft";

const EMPTY_DASHBOARDS: ReadonlyArray<QuicksightDashboard> = [];

type Props = Readonly<{
  email: string;
  /** Reporting permission draft; only `QUICKSIGHT_DASHBOARDS` is synced from here. */
  activePermissions?: ReadonlyArray<UserPermissionEnum>;
  /** User-detail page: local draft + handlers for Save; modal uses immediate API saves. */
  deferSave?: boolean;
  onDeferredSaveHandlersChange?: (
    handlers: QuicksightDashboardDeferredSaveHandlers | null,
  ) => void;
  onSetPermission?: (permissions: ReadonlyArray<UserPermissionEnum>) => void;
}>;

/**
 * Quicksight access is per dashboard ID. Keeps reporting enum `QUICKSIGHT_DASHBOARDS`
 * in sync when the user has at least one dashboard selected.
 */
export const QuicksightDashboardsMacroCheckbox = ({
  email,
  activePermissions,
  deferSave = false,
  onDeferredSaveHandlersChange,
  onSetPermission,
}: Props) => {
  const isResideAdmin = useSelector((state: AppState) =>
    select.adminSession.isResideAdmin(state),
  );
  const emails = useMemo(() => (email ? [email] : []), [email]);
  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useQuicksightUsersDashboards(emails);
  const dashboardOptions = data?.dashboardOptions ?? EMPTY_DASHBOARDS;
  const userDashboards =
    (email && data?.dashboardsByEmail[email]) || EMPTY_DASHBOARDS;

  const {mutate, isLoading: isSavingPermissions} =
    useQuicksightDashboardsPermissionMutation({
      onConflict: () => {
        refetch();
      },
    });

  const loading = isLoading || isFetching;
  const statsReady = isQuicksightHeaderStatsReady(email, isLoading);
  const dashboardIds = useMemo(
    () =>
      dashboardOptions
        .map(({id}) => id)
        .filter((id): id is string => Boolean(id)),
    [dashboardOptions],
  );

  const savedCheckedValues = useMemo(
    () => userDashboards.map(dashboard => dashboard.id),
    [userDashboards],
  );

  const onPersistSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const persistDashboardChanges = useCallback(
    ({
      email: targetEmail,
      dashboardIds,
      selected,
    }: {
      email: string;
      dashboardIds: ReadonlyArray<string>;
      selected: boolean;
    }) =>
      new Promise<void>((resolve, reject) => {
        mutate(
          {
            email: targetEmail,
            dashboardIds,
            selected,
            onSuccess: () => {
              resolve(undefined);
            },
          },
          {
            onError: reject,
          },
        );
      }),
    [mutate],
  );

  const dashboardDraft = useQuicksightDashboardDraft({
    email,
    savedCheckedIds: savedCheckedValues,
    persistDashboardChanges,
    onPersistSuccess,
  });

  const checkedSource = deferSave
    ? dashboardDraft.checkedValues
    : savedCheckedValues;
  const dashboardSelection = useMemo(
    () => getQuicksightDashboardSelection(dashboardIds, checkedSource),
    [checkedSource, dashboardIds],
  );
  const {checkedValues, everyChecked, selectedCount, someChecked, totalCount} =
    dashboardSelection;

  // Latest save/discard fns for stable callbacks passed to the parent effect.
  const deferredSaveHandlersRef = useRef<QuicksightDashboardDeferredSaveHandlers | null>(
    null,
  );

  // Serializable slice shared by ref (render) and onDeferredSaveHandlersChange (effect).
  const deferredSaveMeta = useMemo(
    () => ({
      pendingChangeCount: dashboardDraft.pendingChangeCount,
      selectedCount,
      statsReady,
      totalCount,
    }),
    [
      dashboardDraft.pendingChangeCount,
      selectedCount,
      statsReady,
      totalCount,
    ],
  );

  if (deferSave) {
    deferredSaveHandlersRef.current = {
      ...deferredSaveMeta,
      discard: dashboardDraft.discard,
      save: dashboardDraft.save,
    };
  } else {
    deferredSaveHandlersRef.current = null;
  }

  useEffect(() => {
    if (!deferSave) {
      onDeferredSaveHandlersChange?.(null);
      return;
    }

    onDeferredSaveHandlersChange?.({
      ...deferredSaveMeta,
      discard: () => {
        deferredSaveHandlersRef.current?.discard();
      },
      save: async () => {
        await deferredSaveHandlersRef.current?.save();
      },
    });
  }, [deferSave, deferredSaveMeta, onDeferredSaveHandlersChange]);

  const syncReportingPermission = (hasDashboardAccess: boolean) => {
    if (!onSetPermission || !activePermissions) {
      return;
    }

    const nextPermissions = syncQuicksightDashboardsReportingPermission(
      hasDashboardAccess,
      activePermissions,
    );

    if (nextPermissions !== activePermissions) {
      onSetPermission(nextPermissions);
    }
  };

  const willHaveDashboardAccess = (
    nextCheckedValues: ReadonlyArray<string>,
  ) => nextCheckedValues.length > 0;

  const applyDashboardChecked = (
    dashboardId: string,
    checked: boolean,
  ) => {
    if (deferSave) {
      dashboardDraft.setDashboardChecked(dashboardId, checked);

      const nextCheckedValues = checked
        ? [...checkedValues.filter(id => id !== dashboardId), dashboardId]
        : checkedValues.filter(id => id !== dashboardId);

      syncReportingPermission(willHaveDashboardAccess(nextCheckedValues));

      return;
    }

    const nextCheckedValues = checked
      ? [...checkedValues.filter(id => id !== dashboardId), dashboardId]
      : checkedValues.filter(id => id !== dashboardId);

    mutate({
      email,
      dashboardIds: [dashboardId],
      selected: checked,
      onSuccess: () => {
        syncReportingPermission(willHaveDashboardAccess(nextCheckedValues));
        refetch();
      },
    });
  };

  const onGroupChange = (event: FormEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked;
    const dashboardsToUpdate = dashboardIds.filter(
      dashboardId => checked !== checkedValues.includes(dashboardId),
    );

    if (dashboardsToUpdate.length === 0) {
      return;
    }

    if (deferSave) {
      dashboardDraft.setGroupChecked(dashboardIds, checked);
      syncReportingPermission(checked);
      return;
    }

    syncReportingPermission(checked);

    mutate({
      email,
      dashboardIds: dashboardsToUpdate,
      selected: checked,
      onSuccess: () => {
        refetch();
      },
    });
  };

  const label =
    !deferSave && isSavingPermissions
      ? "Quicksight Dashboards (Saving...)"
      : "Quicksight Dashboards";

  return (
    <PermissionCategoryCard
      label={label}
      selectedCount={selectedCount}
      totalCount={totalCount}
      someChecked={someChecked}
      everyChecked={everyChecked}
      disabled={loading || (!deferSave && isSavingPermissions)}
      headerActions={
        isResideAdmin ? (
          <SyncQuicksightDashboardsButton onSuccess={refetch} />
        ) : undefined
      }
      onGroupChange={onGroupChange}
    >
      {loading ? (
        <LoadingState>Loading...</LoadingState>
      ) : (
        dashboardOptions.map(({id: dashboardId, name}) => (
          <PermissionCategoryItem key={dashboardId}>
            <CheckboxItem
              value={dashboardId}
              label={name}
              checked={checkedValues.includes(dashboardId)}
              disabled={loading || (!deferSave && isSavingPermissions)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                applyDashboardChecked(
                  dashboardId,
                  event.currentTarget.checked,
                )
              }
            />
          </PermissionCategoryItem>
        ))
      )}
    </PermissionCategoryCard>
  );
};

const LoadingState = styled(Text)`
  color: ${theme.color.gray150};
  display: block;
  font-size: 14px;
  line-height: 20px;
  padding: 16px;
`;
